<template>
  <div class="min-h-screen flex flex-col bg-background text-foreground font-sans antialiased">
    <!-- Persistent Base Header -->
    <!-- The header is the page's TOP RULE: flat, the card's plane, one border
         under it. No blur, no shadow — a header that floats over the page is
         a second surface, and the theme has one. -->
    <header class="sticky top-0 z-40 w-full border-b bg-card shrink-0">
      <!-- ⭐ THE HEADER IS TWO COLUMNS, and the left one is the RAIL's.
           It used to be one flex row with a divider that fell wherever the
           logotype happened to end, so the eye was offered two column-starts
           and believed neither. The brand block is exactly the rail's width
           with the same border-r, so the header's rule IS the rail: one
           vertical line from the top of the screen to the bottom, with the
           mark sitting on the column like its heading. Everything right of it
           starts at the content's own gutter (design 80). 56 px tall: a title
           bar, not a banner. -->
      <div class="flex h-14 items-stretch">
        <router-link
          to="/"
          class="flex w-60 shrink-0 items-center border-r px-5"
          data-testid="brand-home"
        >
          <BrandMark :size="30" />
        </router-link>

        <div class="flex min-w-0 flex-1 items-center gap-3 px-6">
          <!-- ⭐ THE PAGE'S NAME LEADS THE BAR (his ruling 2026-10-10, reversing
               the old « no page name in the header »): every page sends its
               title through PageTitle, and a RECORD sends its breadcrumb —
               « Travaux › J-1549 » — which is the one name the rail can never
               show. It replaces a « Retour » row per page: the parent link is
               where the page lives, the browser's back is where you came from. -->
          <div :id="PAGE_TITLE_HOST_ID" class="flex min-w-0 items-center" />

          <span class="flex-1"></span>

          <!-- THE VERB, then the account: « Nouveau » sits at the right edge
               beside the account cluster (his layout, 2026-10-10) — the one
               create door, the same place on every page, the only orange in
               the chrome. A non-admin has no door; the cluster stands alone. -->
          <!-- « Configuration » — the org's setup gaps, admins and owners only,
               and only while something is left to set up (it renders nothing
               otherwise). -->
          <SetupCheckButton v-if="auth.isAuthenticated && isAdmin" />
          <IntakeMenu v-if="auth.isAuthenticated && isAdmin" />

          <!-- ⭐ THE ACCOUNT CLUSTER (his inversion, 2026-09-13): the customer's
               identity and the user's, together, against the right edge.
               One trigger, not three: the menu behind it already carries the org
               settings AND « changer d'organisation », so a second door on the
               org square would be two ways to do one thing. -->
          <UserMenu v-if="auth.isAuthenticated" />
        </div>
      </div>
    </header>

    <!-- Main View Slot -->
    <router-view />

    <!-- What the header's doors open, mounted ONCE for the whole console: a
         global door needs a global host, and the dialogs used to live inside
         the two pages that carried the buttons. Outside the router view on
         purpose — a navigation mid-capture must not unmount the form. -->
    <IntakeDialogs v-if="auth.isAuthenticated && isAdmin" />
  </div>
</template>

<script setup lang="ts">
import BrandMark from '@/components/layout/BrandMark.vue'
import UserMenu from '@/components/layout/UserMenu.vue'
import IntakeMenu from '@/components/console/IntakeMenu.vue'
import SetupCheckButton from '@/components/layout/SetupCheckButton.vue'
import IntakeDialogs from '@/components/console/IntakeDialogs.vue'
import { provide } from 'vue'
import { usePermissions } from '@/composables/usePermissions'
import { useAuthStore } from '@/stores/auth'
import { PAGE_TITLE_HOST, PAGE_TITLE_HOST_ID } from '@/components/layout/pageTitleHost'

const { isAdmin } = usePermissions()
const auth = useAuthStore()

// This shell renders the header's title slot — every PageTitle below teleports.
provide(PAGE_TITLE_HOST, true)
</script>
